import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { Box } from '@mui/material'
import { getFieldData, getField, getTranslation, getPagePermissionByPath } from '../utils/utils'
import { getRestaurantPermissionsAllSections } from '../utils/restaurantPermissions'
import { GetScreenSize } from '../utils/screenSize'
import actions from '../redux/actions/RestActions'
import branchActions from '../redux/actions/BranchActions'
import ContextSelect, { initialsOf } from './ContextSelect'

// Unified restaurant/branch header selector — see
// docs/UNIFIED_RESTAURANT_BRANCH_SELECTOR.md for the full design spec this
// was extracted from.
//
// Connected to Redux via mapStateToProps / mapDispatchToProps so callers can render
// <RestaurantBranchSelector /> with minimal props while staying self-reliant.
// Supports overriding props if explicitly provided.
const getOptImg = (opt) => opt?.logo || opt?.image || opt?.imageUrl || opt?.avatar || opt?.icon || ''

/** The page's "all branches" entry, under any of the uuids pages give it. */
const isAllBranchesOption = (branch) => ['0', 'all', 'all-branches'].includes(String(branch?.uuid))

function RestaurantBranchSelector({
  language: propLanguage,
  dictionary: propDictionary,
  restaurants: propRestaurants,
  branches: propBranches,
  selectedRestaurant: propSelectedRestaurant,
  onRestaurantChange,
  onRestaurantBlur,
  selectedBranch: propSelectedBranch,
  onBranchChange,
  showAllBranchesOption = false,
  /** Adds "All restaurants" — only for pages that can list every restaurant at once. */
  showAllRestaurantsOption = false,
  multiple = false,
  hideRestaurantSelect = false,
  hideBranchSelect: propHideBranchSelect,
  disabled = false,
  sideNavState: propSideNavState,
  wideBreakpoint = 700,
  /** Phones: the full-width row under the header (the headers' ContextBar; ContextSelect `bar`). */
  bar = false,
  design2 = false,
  /** `{ options, selected, onChange(option), label(option), keyOf(option) }`: a currency shown in the same control. */
  currency,

  // Redux connected props
  reduxLanguage,
  reduxTranslation,
  reduxRestaurants = [],
  reduxSelectedRestaurant = '',
  reduxSelectedRestDashboard = '',
  reduxRestaurantsBranches = [],
  reduxPermissionTrees = {},
  reduxRestaurantAccess = [],
  reduxBranches = [],
  reduxSelectedBranch = '',
  reduxSideNavState = false,
  dispatchSetSelectedRestaurant,
  dispatchSetDashboardRest,
  dispatchSetSelectedBranch,
}) {
  const screenSize = GetScreenSize()
  const isWide = screenSize.dynamicWidth > wideBreakpoint

  const language = propLanguage || reduxLanguage
  const dictionary = useMemo(() => {
    if (propDictionary && Object.keys(propDictionary).length > 0) {
      return propDictionary
    }
    const filterDict = getTranslation('resto-branch-filter', reduxTranslation)
    if (filterDict && Object.keys(filterDict).length > 0) {
      return filterDict
    }
    return getTranslation('dashboard', reduxTranslation) || {}
  }, [propDictionary, reduxTranslation])

  const sideNavState = propSideNavState !== undefined ? propSideNavState : reduxSideNavState
  const selectedRestaurant =
    propSelectedRestaurant !== undefined && propSelectedRestaurant !== ''
      ? propSelectedRestaurant
      : reduxSelectedRestaurant || reduxSelectedRestDashboard || ''

  const selectedBranch =
    propSelectedBranch !== undefined && propSelectedBranch !== ''
      ? propSelectedBranch
      : reduxSelectedBranch || (showAllBranchesOption ? '0' : '')

  const restaurants = useMemo(() => {
    let final = []
    if (propRestaurants && propRestaurants.length > 0) {
      final = [...propRestaurants]
    } else {
      // `allRestaurantsRoles` is never populated when this app runs embedded and shares
      // its Redux store with the parent admin app (its getRest() never dispatches
      // ALL_RESTAURANTS_ROLES), which left every restaurant flagged isDisabled here.
      // Derive access straight from permissionTrees/restaurantAccess instead — same
      // source the host admin app itself uses, and always populated on GET_REST_SUCCESS.
      (reduxRestaurants || []).forEach((rest) => {
        let obj = { ...rest }
        const pages = getRestaurantPermissionsAllSections(reduxPermissionTrees, reduxRestaurantAccess, rest.uuid)
        const target = pages.length > 0 ? getPagePermissionByPath(pages, window.location.pathname) : undefined
        final.push({ ...obj, isDisabled: !Boolean(target) })
      })
    }
    // A page that cannot list every restaurant at once must not offer it: the pick would just snap back.
    if (showAllRestaurantsOption && final.length > 1 && !final.some((r) => r.uuid === 'all-restaurants')) {
      final.unshift({ uuid: 'all-restaurants' })
    }
    return final
  }, [propRestaurants, reduxRestaurants, reduxRestaurantsBranches, reduxPermissionTrees, reduxRestaurantAccess, showAllRestaurantsOption])

  const branches = useMemo(() => {
    let final = []
    if (propBranches && propBranches.length > 0) {
      final = [...propBranches]
    } else {
      if (selectedRestaurant && selectedRestaurant !== 'all-restaurants' && reduxRestaurantsBranches?.length > 0) {
        let target = reduxRestaurantsBranches.find((resto) => String(resto.uuid) === String(selectedRestaurant))
        if (target && target.branches && target.branches.length > 0) {
          final = [...target.branches]
        }
      }
      if (final.length === 0 && reduxBranches && reduxBranches.length > 0) {
        final = [...reduxBranches]
      }
    }
    if (showAllBranchesOption && !final.some((b) => String(b.uuid) === '0' || b.uuid === 'all-branches')) {
      final.unshift({ uuid: '0' })
    }
    return final
  }, [propBranches, selectedRestaurant, reduxRestaurantsBranches, reduxBranches, showAllBranchesOption])

  const hideBranchSelect =
    propHideBranchSelect !== undefined
      ? propHideBranchSelect
      : selectedRestaurant === 'all-restaurants' || branches.length === 0

  const handleRestaurantChange = (uuid) => {
    if (onRestaurantChange) {
      onRestaurantChange(uuid)
    }
    if (uuid !== 'all-restaurants') {
      if (dispatchSetSelectedRestaurant) dispatchSetSelectedRestaurant(uuid)
    }
    if (dispatchSetDashboardRest) dispatchSetDashboardRest(uuid)
  }

  const handleBranchSelectChange = (uuidOrUuids) => {
    if (onBranchChange) {
      onBranchChange(uuidOrUuids)
    }
    if (dispatchSetSelectedBranch && typeof uuidOrUuids === 'string') {
      dispatchSetSelectedBranch(uuidOrUuids)
    }
  }

  const restaurantOptions = hideRestaurantSelect ? [] : restaurants
  /**
   * "All branches" is only for someone who reaches every branch of the restaurant (`hasAllBranches`
   * on /admin/restaurant/branches). Anyone limited to some of them picks among those, and a
   * single-choice page is moved onto the first one (the effect below).
   */
  const accessEntry = (reduxRestaurantsBranches || []).find((resto) => String(resto.uuid) === String(selectedRestaurant))
  const accessibleUuids =
    accessEntry?.hasAllBranches === false ? new Set((accessEntry.branches || []).map((branch) => String(branch.uuid))) : null
  const realBranches = branches.filter(
    (branch) => !isAllBranchesOption(branch) && (!accessibleUuids || accessibleUuids.has(String(branch.uuid)))
  )
  const restrictedBranches = !hideBranchSelect && Boolean(accessibleUuids) && realBranches.length > 0
  const branchOptions = hideBranchSelect ? [] : restrictedBranches ? realBranches : branches
  const selectedBranchArray = multiple ? (selectedBranch || []) : (selectedBranch !== undefined && selectedBranch !== null ? [selectedBranch] : [])
  const selectedRestObj =
    restaurantOptions.find((r) => String(r.uuid) === String(selectedRestaurant)) ||
    (!selectedRestaurant && restaurantOptions.length > 0 ? (restaurantOptions.find((r) => !r.isDisabled) || restaurantOptions[0]) : null)
  const matchedBranchObjs = branchOptions.filter((b) => selectedBranchArray.map(String).includes(String(b.uuid)))
  // A limited user's "all" (nothing picked yet) reads as the branches they can reach.
  const selectedBranchObjs =
    restrictedBranches && matchedBranchObjs.length === 0 ? (multiple ? realBranches : realBranches.slice(0, 1)) : matchedBranchObjs

  const selectedBranchKey = multiple ? '' : String((selectedBranch && typeof selectedBranch === 'object' ? selectedBranch.uuid : selectedBranch) ?? '')
  const fallbackBranchUuid =
    restrictedBranches && !multiple && !realBranches.some((branch) => String(branch.uuid) === selectedBranchKey)
      ? String(realBranches[0].uuid)
      : ''
  useEffect(() => {
    if (!fallbackBranchUuid || disabled) return
    handleBranchSelectChange(fallbackBranchUuid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallbackBranchUuid, disabled])

  const allBranchesLabel = getField(dictionary, 'all-branches', language) || 'All Branches'
  const restaurantLabel = (o) =>
    o?.uuid === 'all-restaurants' ? getField(dictionary, 'all-restaurants', language) || 'All Restaurants' : getFieldData(o, 'name', language)
  const branchLabel = (o) => (isAllBranchesOption(o) ? allBranchesLabel : getFieldData(o, 'name', language) || String(o?.uuid))

  const showsRestaurant = restaurantOptions.length > 0
  const restaurantTitle = showsRestaurant && selectedRestObj ? restaurantLabel(selectedRestObj) : ''
  const hasBranches = branchOptions.length > 0
  const selectedBranchKeys = selectedBranchObjs.map((branch) => String(branch.uuid))
  // Multi-select with nothing picked reads as every branch (the old field's placeholder).
  const branchSummary = !hasBranches
    ? ''
    : multiple
      ? selectedBranchObjs.length
        ? selectedBranchObjs.map(branchLabel).join(', ')
        : allBranchesLabel
      : selectedBranchObjs[0]
        ? branchLabel(selectedBranchObjs[0])
        : ''
  const currencyOptions = currency?.options || []
  const currencyLabel = currency?.selected ? currency.label(currency.selected) : ''

  const toggleBranch = (branch) => {
    const current = selectedBranchObjs.map((b) => b.uuid)
    const next = selectedBranchKeys.includes(String(branch.uuid))
      ? current.filter((uuid) => String(uuid) !== String(branch.uuid))
      : [...current, branch.uuid]
    handleBranchSelectChange(next)
  }

  const sections = [
    restaurantOptions.length > 1 && {
      key: 'restaurant',
      items: restaurantOptions.map((restaurant) => ({
        key: String(restaurant.uuid),
        label: restaurantLabel(restaurant),
        caption: restaurant.isDisabled ? getField(dictionary, 'no-access', language) || 'No Access' : '',
        disabled: Boolean(restaurant.isDisabled),
        selected: String(restaurant.uuid) === String(selectedRestObj?.uuid),
        image: getOptImg(restaurant),
        onSelect: () => handleRestaurantChange(restaurant.uuid),
      })),
    },
    hasBranches && {
      key: 'branch',
      multiple,
      items: [
        ...(multiple && !restrictedBranches && !branchOptions.some(isAllBranchesOption)
          ? [{ key: '__all', label: allBranchesLabel, icon: 'all', selected: selectedBranchObjs.length === 0, onSelect: () => handleBranchSelectChange([]) }]
          : []),
        ...branchOptions.map((branch) => ({
          key: String(branch.uuid),
          label: branchLabel(branch),
          icon: isAllBranchesOption(branch) ? 'all' : 'pin',
          image: getOptImg(branch),
          selected: selectedBranchKeys.includes(String(branch.uuid)),
          onSelect: () => (multiple ? toggleBranch(branch) : handleBranchSelectChange(branch.uuid)),
        })),
      ],
    },
    currencyOptions.length > 1 && {
      key: 'currency',
      chips: true,
      items: currencyOptions.map((option) => ({
        key: String(currency.keyOf(option)),
        label: currency.label(option),
        selected: Boolean(currency.selected) && currency.keyOf(option) === currency.keyOf(currency.selected),
        onSelect: () => currency.onChange(option),
      })),
    },
  ].filter(Boolean)

  /**
   * The panel's search. Its words always come from the selector's own dictionary — a page may hand
   * this control its own dictionary, which has no search wording.
   */
  const filterDictionary = getTranslation('resto-branch-filter', reduxTranslation)
  const searchesRestaurants = sections.some((section) => section.key === 'restaurant')
  const searchesBranches = sections.some((section) => section.key === 'branch')
  const search = {
    placeholder: searchesRestaurants && searchesBranches
      ? getField(filterDictionary, 'search-restaurants-branches', language)
      : searchesRestaurants
        ? getField(filterDictionary, 'search-restaurants', language)
        : getField(filterDictionary, 'search-branches', language),
    empty: getField(filterDictionary, 'no-matches', language),
    clear: getField(filterDictionary, 'clear-search', language),
  }

  // Restaurant first; a page without one (or whose header already names it) leads with its branch.
  const title = restaurantTitle || branchSummary
  if (!title) return null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flex: bar ? '1 1 auto' : '0 1 auto',
        minWidth: 0,
        marginLeft: isWide && !bar ? '4px' : 0,
        paddingLeft: !isWide && !bar ? '6px' : 0,
      }}
    >
      <ContextSelect
        title={title}
        subtitle={[restaurantTitle ? branchSummary : '', currencyLabel].filter(Boolean).join(' · ')}
        avatar={restaurantTitle ? { image: getOptImg(selectedRestObj), initials: initialsOf(restaurantTitle) } : { icon: 'pin' }}
        sections={sections}
        compact={!isWide && !bar}
        bar={bar}
        disabled={disabled}
        surface={design2 ? 'paper' : 'default'}
        search={search}
      />
    </Box>
  )
}

const mapStateToProps = (state) => ({
  reduxLanguage: state.translation?.language,
  reduxTranslation: state.translation?.translation,
  reduxRestaurants: state.rest?.restaurants,
  reduxSelectedRestaurant: state.rest?.selectedRest,
  reduxSelectedRestDashboard: state.dashboard?.selectedRest,
  reduxRestaurantsBranches: state.user?.restaurantsBranches,
  reduxPermissionTrees: state.rest?.permissionTrees,
  reduxRestaurantAccess: state.rest?.restaurantAccess,
  reduxBranches: state.branch?.branches || state.branch?.allBranches || [],
  reduxSelectedBranch: state.branch?.selectedBranch,
  reduxSideNavState: state.layout?.settings?.layoutSettings?.sidebar?.open,
})

const mapDispatchToProps = (dispatch) => ({
  dispatchSetSelectedRestaurant: (uuid) => dispatch(actions.setSelectedRest(uuid)),
  dispatchSetSelectedBranch: (uuid) => dispatch(branchActions.setSelectedBranch(uuid)),
})

export default connect(mapStateToProps, mapDispatchToProps)(RestaurantBranchSelector)
